import { useState } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import RenderSvg from "../../../components/renderSvg/RenderSvg";
import ApprovalModal from "../../../features/claimsNew/modals/ApprovalModal";
import FormHeader from "../../dms_common/FormHeader";
import { renderDetailView } from "./DisplayVisibilityCustomFields";
import { formatDecimal } from "../../../components/dms_common/DmsUtils";
import "./DisplayVisibilityClaimSummary.css";
import '../../../features/claimsNew/pages/NewClaimSummary.css';
import walletImageSrc from "../../../assets/image/png/wallet.png";
import { determineClaimTypes, getTotalAmount, ClaimDetail } from "../../../features/claimsNew/utils/utils";

const DisplayVisibilityClaimSummary = () => {
    const [approvalModalOpen, setApprovalModalOpen] = useState<boolean>(false);
    const [approvalModalState, setApprovalModalState] = useState<number>(1);
    const navigate = useNavigate();
    const location = useLocation();

    const locationState: any = location.state as any;
    const [selectedRows, setSelectedRows] = useState<any[]>(locationState?.selectedRows || []);
    const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
    const { currency, claimTypes, claimSubTypes } = ConfigDictionary;
    const backNavigationPath = location.pathname.includes('displayVisibility') ? "displayVisibility" : "assetInstallation";
    const { claimType, subClaimType } = determineClaimTypes(backNavigationPath, claimTypes, claimSubTypes);

    const getISOConvertedSelectedRows = () => {
        return selectedRows.map((item) => {
            let updatedDateFields = {};
            updatedDateFields = backNavigationPath === "assetInstallation" ? {
                claimDate: item?.claimDate?.$d?.toISOString() ?? item?.claimDate,
                installationDate: item?.installationDate?.$d?.toISOString() ?? item?.installationDate
            } : {
                claimDate: item?.claimDate?.$d?.toISOString() ?? item?.claimDate,
                displayStartDate: item?.displayStartDate?.$d?.toISOString() ?? item?.displayStartDate,
                displayEndDate: item?.displayEndDate?.$d?.toISOString() ?? item?.displayEndDate
            };

            return {
                ...item,
                ...updatedDateFields
            };
        });
    };

    const rows = getISOConvertedSelectedRows();

    const getClaimSummaryArr = (): Array<{ label: string; value: string | number }> => {
        const baseArr: { label: string; value: string | number }[] = [
            {
                label: "Claim Type",
                value: claimType,
            },
            {
                label: "Type of Expense",
                value: subClaimType
            },
            {
                label: "Final Claim Amount",
                value: getTotalAmount(rows, "totalClaimAmount")
            },
        ];
        return baseArr;
    };

    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        return date.toISOString().split('T')[0];
    };

    const getClaimDetails = (): ClaimDetail[] => {
        const claimDetails: ClaimDetail[] = [];
        rows.forEach((row: any) => {
            if (backNavigationPath === "displayVisibility") {
                claimDetails.push({
                    claimedAmount: row.totalClaimAmount,
                    approvedAmount: 0,
                    remark: row.remarks,
                    outletCode: row.outletCode,
                    outletName: row.outletName,
                    installationCost: row.installationCost,
                    displayCost: row.displayCost,
                    displayStartDate: row.displayStartDate ? formatDate(row.displayStartDate) : '',
                    displayEndDate: row.displayEndDate ? formatDate(row.displayEndDate) : '',
                    assetName: row.bannerName,
                    batchCode: row.outletCode || '',
                    blobUrl: row.blobUrls,
                    referenceNumber: '',
                    extendedAttributes: { skus: row.skus }
                });
            } else if (backNavigationPath === "assetInstallation") {
                claimDetails.push({
                    claimedAmount: row.totalClaimAmount,
                    approvedAmount: 0,
                    remark: row.remarks,
                    outletCode: row.outletCode,
                    outletName: row.outletName,
                    installationCost: row.installationCost,
                    displayCost: row.displayCost,
                    displayStartDate: row.installationDate ? formatDate(row.installationDate) : '',
                    assetType: row.skuCode,
                    assetId: row.skuName,
                    assetQty: row.quantity,
                    batchCode: row.skuCode || '',
                    blobUrl: row.blobUrls,
                    referenceNumber: '',
                });
            }
        });
        return claimDetails;
    };

    const claimDetails = getClaimDetails();
    const claimSummaryArr = getClaimSummaryArr();
    const loginId = JSON.parse(localStorage.authContext)?.user?.loginId;
    const supplierName = JSON.parse(localStorage.authContext)?.user?.name;

    const claimSummaryObj = {
        claimType: (claimSummaryArr[0]?.value?.toString() || "").toUpperCase().replace(/\s+/g, ""),
        claimSubType: (claimSummaryArr[1]?.value === "Display/Visibility" ? "Banner" : "Asset").toUpperCase().replace(/\s+/g, ""),
        claimTotalValue: claimSummaryArr[2]?.value || "",
        claimApprovedValue: 0,
        claimApprovalDate: "",
        supplier: loginId,
        supplierName,
        status: "REQUESTED",
    };

    const postBody = { ...claimSummaryObj, claimDetails };

    const handleBackNavigation = (idx?: number) => {
        navigate(`/dashboard/claims/create/${backNavigationPath}`, {
            state: { selectedRows: rows, claimSummary: claimSummaryArr, selectedIndex: idx },
        });
    };

    return (
        <div className="create-claim-root">
            <div className="claim-summary-heading heading">
                <FormHeader
                    parentLabel={"Create New Claim"}
                    label={"Claim Summary"}
                    goBackToParent={() => handleBackNavigation()}
                />
            </div>

            <div className="claim-summary-container">
                <div className="summary-container">
                    <div className="claim-summary-header">
                        <p className="claim-summary-title">
                            <div className="confirmed_order_icon">
                                <RenderSvg name="orderConfirmedIcon" />
                            </div>
                            Claim Summary
                        </p>
                    </div>
                    <div className="claim-summary-main">
                        {renderDetailView(
                            'summary',
                            claimSummaryArr,
                            [],
                            currency,
                            backNavigationPath,
                            handleBackNavigation
                        )}
                    </div>
                </div>

                <div className="sku-details-container">
                    <div className="sku-details-header">
                        <p className="claim-summary-title">
                            <div className="confirmed_order_icon">
                                <RenderSvg name="orderConfirmedIcon" />
                            </div>
                            Outlet Details
                        </p>
                    </div>

                    <div className="sku-details-main">
                        {renderDetailView(
                            'outletDetails',
                            claimSummaryArr,
                            rows,
                            currency,
                            backNavigationPath,
                            handleBackNavigation
                        )}
                    </div>
                </div>

                <footer className="claim-summary-footer">
                    <div className="claim-f-left">
                        <div className="wallet-icon">
                            <p className="wallet-qty">
                                {backNavigationPath === "displayVisibility" ? rows.length : rows.reduce((sum, row) => sum + (row.quantity || 0), 0)}
                            </p>
                            <img style={{ width: "33px" }} src={walletImageSrc} />
                        </div>
                        <p className="total-quan-text">
                            Total Quantity :&nbsp;
                            <p className="total-quan-span">
                                {backNavigationPath === "displayVisibility" ? rows.length : rows.reduce((sum, row) => sum + (row.quantity || 0), 0)}
                            </p>
                        </p>
                        <p className="total-quan-text">
                            Final Claim Amount :&nbsp;
                            <p className="total-quan-span">
                                {currency} {typeof claimSummaryObj.claimTotalValue === 'number' ? formatDecimal(claimSummaryObj.claimTotalValue) : claimSummaryObj.claimTotalValue}
                            </p>
                        </p>
                    </div>
                    <div className="claim-f-right">
                        <button
                            className="button-save-claim"
                            onClick={() => setApprovalModalOpen(true)}
                            disabled={!rows.length}
                        >
                            <span className="button-save-text-claim">Raise for Approval</span>
                        </button>
                    </div>
                </footer>

                <ApprovalModal
                    approvalModalOpen={approvalModalOpen}
                    setApprovalModalOpen={setApprovalModalOpen}
                    approvalModalState={approvalModalState}
                    setApprovalModalState={setApprovalModalState}
                    postBody={postBody}
                />
            </div>
        </div>
    );
};

export default DisplayVisibilityClaimSummary;